/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-restricted-globals */

import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import Cookies from 'js-cookie';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import NumberFormat from 'react-number-format';

import api from '../../../api/api';
import PayPalButtons from '../../../components/PayPalButtons/PayPalButtons';
import PayPalErrorBoundary from '../../../components/PayPalErrorBoundary/PayPalErrorBoundary';
import usePayPalAvailability from '../../../hooks/usePayPalAvailability';
import ApplePayButton from '../../../components/ApplePayButton/ApplePayButton';
import GooglePayButton from '../../../components/GooglePayButton/GooglePayButton';
import NmiApplePayButton from '../../../components/NmiApplePayButton/NmiApplePayButton';
import NmiGooglePayButton from '../../../components/NmiGooglePayButton/NmiGooglePayButton';
import Banner from '../../../components/Banner/Banner';

import {
  buildQueryParams,
  getMaxDiscount,
  getPriceWithCurrency,
  getSavingsPercent,
} from '../../../utils/helpers';
import { states } from '../../../utils/states';
import { Context as ReviewsContext } from '../../../context/ReviewsContext';

import ssl from './img/ssl.png';
import sat from './img/sat.png';
import flag from './img/flag.png';
import norton from './img/norton.png';
import paypal from './img/paypal.png';
import cc from './img/cc.png';
import img1 from './img/1.webp';
import img2 from './img/2.webp';
import img3 from './img/3.webp';
import img4 from './img/4.webp';

import styles from './Bug.module.sass';

function getFbcValue(queryParams) {
  if (Cookies.get('_fbc')) {
    return Cookies.get('_fbc');
  }

  if (queryParams.fbclid) {
    const timestamp = new Date().getTime();
    return `fb.1.${timestamp}.${queryParams.fbclid}`;
  }

  return '';
}

const validationMessages = {
  email: 'Enter valid email address.',
  phone: 'Enter valid phone number.',
  creditCardNumber: 'Enter valid credit card number.',
  firstName: 'First name is required.',
  lastName: 'Last name is required.',
  shippingAddress1: 'Shipping address is required.',
  shippingCity: 'City is required.',
  shippingState: 'This Field is required.',
  shippingZip: 'ZIP code is required.',
  expirationMonth: 'Select a valid expiration month.',
  expirationYear: 'Select a valid expiration year.',
  CVV: 'Enter valid CVV.',
  isTermsAgreed: 'You must agree to the terms.',
  billingFirstName: 'First name is required.',
  billingLastName: 'Last name is required.',
  billingAddress1: 'Billing address is required.',
  billingCity: 'City is required.',
  billingState: 'This Field is required.',
  billingZip: 'ZIP code is required.',
};

// Validate a single field
function validateField(fieldName, value, shippingCountry, billingCountry) {
  switch (fieldName) {
    case 'email':
      const emailFormatRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!value || !emailFormatRegex.test(value)) {
        return validationMessages.email;
      }

      const domainPart = value.substring(value.lastIndexOf('@') + 1);
      if (domainPart.includes('..')) {
        return validationMessages.email;
      }
      break;
    case 'phone':
      if (!value) {
        return validationMessages.phone;
      }

      if (shippingCountry === 'GB') {
        if (!/^\+44\d{10}$/.test(value.replace(/\s+/g, ''))) {
          return 'Enter a valid UK phone number.';
        }
      } else if (shippingCountry === 'US') {
        if (!/^\+1\d{10}$/.test(value.replace(/\s+/g, ''))) {
          return 'Enter a valid US phone number.';
        }
      }
      break;
    case 'creditCardNumber':
      if (!value || !/^\d{13,19}$/.test(value.replace(/\s+/g, ''))) {
        return validationMessages.creditCardNumber;
      }
      break;
    case 'firstName':
      if (!value) {
        return validationMessages.firstName;
      }
      break;
    case 'lastName':
      if (!value) {
        return validationMessages.lastName;
      }
      break;
    case 'shippingAddress1':
      if (!value) {
        return validationMessages.shippingAddress1;
      }
      break;
    case 'shippingCity':
      if (!value) {
        return validationMessages.shippingCity;
      }
      break;
    case 'shippingState':
      if (!value) {
        return validationMessages.shippingState;
      }
      break;
    case 'shippingZip':
      if (!value) {
        return validationMessages.shippingZip;
      }
      if (shippingCountry === 'US') {
        if (!/^[0-9]+$/.test(value)) {
          return 'US ZIP code must contain only numbers.';
        }
        if (value.length !== 5) {
          return 'US ZIP code must be 5 digits.';
        }
      }
      break;
    case 'billingFirstName':
      if (!value) {
        return validationMessages.billingFirstName;
      }
      break;
    case 'billingLastName':
      if (!value) {
        return validationMessages.billingLastName;
      }
      break;
    case 'billingAddress1':
      if (!value) {
        return validationMessages.billingAddress1;
      }
      break;
    case 'billingCity':
      if (!value) {
        return validationMessages.billingCity;
      }
      break;
    case 'billingState':
      if (!value) {
        return validationMessages.billingState;
      }
      break;
    case 'billingZip':
      if (!value) {
        return validationMessages.billingZip;
      }
      if (billingCountry === 'US') {
        if (!/^[0-9]+$/.test(value)) {
          return 'US ZIP code must contain only numbers.';
        }
        if (value.length !== 5) {
          return 'US ZIP code must be 5 digits.';
        }
      }
      break;
    case 'expirationMonth':
      if (!value || isNaN(value) || value < 1 || value > 12) {
        return validationMessages.expirationMonth;
      }
      break;
    case 'expirationYear':
      if (!value || isNaN(value)) {
        return validationMessages.expirationYear;
      }
      break;
    case 'CVV':
      if (!value || !/^\d{3,4}$/.test(value)) {
        return validationMessages.CVV;
      }
      break;
    case 'isTermsAgreed':
      if (!value) {
        return validationMessages.isTermsAgreed;
      }
      break;
    default:
      break;
  }

  return null;
}

const validateForm = (data, shippingCountry, billingCountry) => {
  const errors = {};
  const fieldsToValidate = { ...validationMessages };

  if (data.billingSameAsShipping) {
    delete fieldsToValidate.billingFirstName;
    delete fieldsToValidate.billingLastName;
    delete fieldsToValidate.billingAddress1;
    delete fieldsToValidate.billingCity;
    delete fieldsToValidate.billingState;
    delete fieldsToValidate.billingZip;
  }

  Object.keys(fieldsToValidate).forEach((field) => {
    const error = validateField(field, data[field], shippingCountry, billingCountry);
    if (error) {
      errors[field] = error;
    }
  });

  if (!errors.expirationMonth && !errors.expirationYear) {
    const expDate = moment(`${data.expirationMonth}-${data.expirationYear}`, 'MM-YYYY', true).endOf('month');
    const currentDate = moment();

    if (!expDate.isValid() || expDate.isBefore(currentDate, 'day')) {
      errors.expirationMonth = validationMessages.expirationMonth;
      errors.expirationYear = validationMessages.expirationYear;
    }
  }
  return errors;
};

export default function Bug({
  offer,
  isLoading,
  crossSells,
  onTermsClick,
  onCreateOrder,
  declineError,
  reviews,
  showReviews,
  isCooldownActive,
}) {
  const {
    state: { reviews: contextReviews = [] },
  } = useContext(ReviewsContext);
  const [banners, setBanners] = useState([]);
  const queryParams = buildQueryParams(location);
  const showPayPal = queryParams.paypal !== '0' && (queryParams.paypal === '1' || offer.showPayPal);
  const showApplePay = queryParams.applepay !== '0' && (queryParams.applepay === '1' || offer.showApplePay);
  const showGooglePay =
    queryParams.googlepay !== '0' && (queryParams.googlepay === '1' || offer.showGooglePay);

  const mergedProductsData = offer.productsData.map((product) => {
    const productDetail = offer.productDetails[product.id];
    return {
      ...product,
      ...productDetail,
    };
  });

  const defaultProduct = mergedProductsData.find(
    (item) => String(item?.id) === String(offer?.defaultVariantId)
  );

  const preSelectedProduct = mergedProductsData.find(
    (item) => String(item?.id) === String(queryParams?.productid)
  );

  const initialProduct = preSelectedProduct || defaultProduct || mergedProductsData[0];

  const [clientToken, setClientToken] = useState(null);

  /* Hook Call */
  const { isPayPalEnabled } = usePayPalAvailability(clientToken);

  const [selectedProduct, setSelectedProduct] = useState(initialProduct);
  const [errors, setErrors] = useState({});
  const [currentSlide, setCurrentSlide] = useState(0);
  const hasPayPal = isPayPalEnabled && showPayPal;
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(hasPayPal ? 'paypal' : 'card');
  const inlineReviewsSource = contextReviews.length > 0 ? contextReviews : reviews || [];
  const inlineReviews = inlineReviewsSource.slice(0, 3);
  const [timeRemaining, setTimeRemaining] = useState(600); // 10 minutes in seconds

  // Slider images - can be from offer.gallery or hardcoded
  const sliderImages = [img1, img2, img3, img4];

  const [state, setState] = useState({
    email: '',
    firstName: '',
    lastName: '',
    shippingAddress1: '',
    shippingCity: '',
    shippingState: 'AL',
    shippingCountry: offer?.shippingCountries[0]?.id || 'US',
    shippingZip: '',
    phone: '',
    creditCardNumber: '',
    expirationMonth: '',
    expirationYear: '',
    CVV: '',
    billingFirstName: '',
    billingLastName: '',
    billingAddress1: '',
    billingCity: '',
    billingState: '',
    billingCountry: 'US',
    billingZip: '',
    billingSameAsShipping: true,
    brand: offer.brand,
    isTermsAgreed: true,
    productId: initialProduct.id,
    productQty: initialProduct.quantity,
    productName: initialProduct.name,
    productPrice: initialProduct.price,
    agreeSms: false,
    fbc: getFbcValue(queryParams),
    fbp: Cookies.get('_fbp'),
    fbclid: queryParams.fbclid || '',
    orderTotal: initialProduct.price * 1 + initialProduct.shippingPrice * 1,
    campaignId: offer.campaignId,
    shippingId: initialProduct.shippingId || '',
    shippingPrice: initialProduct.shippingPrice || '',
    offers: [
      {
        product_id: initialProduct.id,
        offer_id: initialProduct.offerId,
        billing_model_id: initialProduct.billingModelId,
        price: initialProduct.price,
        productQty: initialProduct.quantity,
        productName: initialProduct.name,
        isMainProduct: true,
      },
    ],
  });

  async function getBanners() {
    try {
      const response = await api.get('/banners');
      if (response.data) {
        setBanners(response.data);
      }
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  useEffect(() => {
    if (!offer.isBannerDisabled) {
      getBanners();
    }
  }, []);

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (hasPayPal) {
      setSelectedPaymentMethod('paypal');
    } else {
      setSelectedPaymentMethod('card');
    }
  }, [hasPayPal]);

  useEffect(() => {
    const stickyOffer = {
      billing_model_id: selectedProduct?.billingModelId,
      offer_id: selectedProduct?.offerId,
      product_id: selectedProduct?.id,
      price: selectedProduct?.price,
      productQty: selectedProduct?.quantity,
      productName: selectedProduct?.name,
      isMainProduct: true,
    };

    const currentOffers = state.offers || [];
    const newOffers = currentOffers.filter((offer) => !offer.isMainProduct);
    newOffers.push(stickyOffer);

    const offersTotal = newOffers.reduce((total, offer) => total + offer.price * 1, 0);
    const orderTotal = offersTotal + selectedProduct?.shippingPrice * 1;

    setState({
      ...state,
      orderTotal,
      offers: newOffers,
      shippingId: selectedProduct?.shippingId,
      shippingPrice: selectedProduct?.shippingPrice,
      productId: selectedProduct?.id,
      productQty: selectedProduct?.quantity,
      productName: selectedProduct?.name,
      productPrice: selectedProduct?.price,
    });
  }, [selectedProduct]);

  useEffect(() => {
    if (crossSells.length > 0) {
      // Get preselected cross-sells from offer config
      const preSelectedFromOffer = crossSells.filter((item) =>
        item.preselectedOffers.some((o) => o.id === offer._id)
      );

      // Get preselected cross-sells from URL parameter (supports comma-separated IDs)
      const urlCrossSellIds = queryParams.crosssellid
        ? queryParams.crosssellid.split(',').map((id) => id.trim())
        : [];
      const preSelectedFromUrl = crossSells.filter((item) => urlCrossSellIds.includes(String(item._id)));

      // Combine both sources, avoiding duplicates
      const preSelectedIds = new Set(preSelectedFromOffer.map((item) => item._id));
      const combinedPreSelected = [
        ...preSelectedFromOffer,
        ...preSelectedFromUrl.filter((item) => !preSelectedIds.has(item._id)),
      ];

      if (combinedPreSelected.length > 0) {
        const crossSellOffers = [];

        combinedPreSelected.forEach((item) => {
          let itemToAdd = item;

          if (item.variants && item.variants.length > 0) {
            itemToAdd = item.variants[0];
          }

          crossSellOffers.push({
            billing_model_id: itemToAdd.productBillingModelId,
            offer_id: itemToAdd.productOfferId,
            product_id: itemToAdd.productId,
            price: itemToAdd.price,
            productName: itemToAdd.productName || itemToAdd.name,
            productQty: '1',
            isCrossSell: true,
            ...(item.trial?.enabled && {
              trial: {
                product_id: item.trial.productId,
                price: item.trial.price,
                quantity: item.trial.quantity,
              },
            }),
          });
        });

        setState((prevState) => {
          const currentOffers = prevState.offers || [];
          const offersWithoutPreselectedCrossSells = currentOffers.filter(
            (offer) => !(offer.isCrossSell && offer.preselected)
          );
          const newOffers = [...offersWithoutPreselectedCrossSells, ...crossSellOffers];

          const offersTotal = newOffers.reduce((total, offer) => total + offer.price * 1, 0);
          const orderTotal = offersTotal + selectedProduct?.shippingPrice * 1;

          return {
            ...prevState,
            orderTotal,
            offers: newOffers,
          };
        });
      }
    }
  }, [crossSells]);

  useEffect(() => {
    const merchantAccountId = offer.braintreeMerchantAccountId || offer.payPalMerchantAccountId;
    const needsToken = (showPayPal || showApplePay || showGooglePay) && merchantAccountId;

    if (!needsToken) return;

    (async () => {
      try {
        const { data } = await api.get(`/braintree/token?merchantAccountId=${merchantAccountId}`);
        setClientToken(data.clientToken);
      } catch (error) {
        console.error('Failed to fetch Braintree token:', error);
      }
    })();
  }, [
    offer.braintreeMerchantAccountId,
    offer.payPalMerchantAccountId,
    showPayPal,
    showApplePay,
    showGooglePay,
  ]);

  useEffect(() => {
    if (state.shippingCountry === 'US' || state.shippingCountry === 'CA') {
      setState({ ...state, shippingState: 'AL' });
    } else {
      setState({ ...state, shippingState: '' });
    }
  }, [state.shippingCountry]);

  function handleFieldBlur(fieldName) {
    const errorMessage = validateField(
      fieldName,
      state[fieldName],
      state.shippingCountry,
      state.billingCountry
    );
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: errorMessage,
    }));
  }

  function handleExpressApprove(data) {
    handleCreateOrder(data);
  }

  function handleFormSubmit() {
    if (isCooldownActive) return;

    const validationErrors = validateForm(state, state.shippingCountry, state.billingCountry);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      handleCreateOrder(state);
    } else {
      const firstErrorField = Object.keys(validationErrors)[0];
      const fieldElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (fieldElement) {
        fieldElement.focus();
      }
    }
  }

  function handleCreateOrder(data) {
    const stickyOffer = {
      billing_model_id: selectedProduct?.billingModelId,
      offer_id: selectedProduct?.offerId,
      product_id: selectedProduct?.id,
      price: selectedProduct?.price,
      productQty: selectedProduct?.quantity,
      productName: selectedProduct?.name,
      isMainProduct: true,
    };

    const currentOffers = state.offers || [];
    const newOffers = currentOffers.filter((offer) => !offer.isMainProduct);
    newOffers.push(stickyOffer);

    const offersTotal = newOffers.reduce((total, offer) => total + offer.price * 1, 0);
    const orderTotal = offersTotal + selectedProduct?.shippingPrice * 1;
    const expirationDate = `${state.expirationMonth}${state.expirationYear.slice(-2)}`;

    const orderData = {
      ...data,
      expirationDate,
      orderTotal,
      offers: newOffers,
      shippingId: selectedProduct?.shippingId,
      shippingPrice: selectedProduct?.shippingPrice,
      productId: selectedProduct?.id,
      productQty: selectedProduct?.quantity,
      productName: selectedProduct?.name,
      productPrice: selectedProduct?.price,
    };

    if (!selectedProduct) {
      setErrors({ ...errors, product: 'Please select a product' });
      return;
    } else {
      setErrors({ ...errors, product: null });
    }

    onCreateOrder(orderData);
  }

  function renderStateSelect() {
    if (state.shippingCountry === 'US' || state.shippingCountry === 'CA') {
      return (
        <div className={styles.inputGroup}>
          <div className={styles.formSelect}>
            <select
              name='shippingState'
              value={state.shippingState}
              onChange={(e) => {
                setState({ ...state, shippingState: e.target.value });
                setErrors((prevErrors) => ({ ...prevErrors, shippingState: null }));
              }}
              onBlur={() => handleFieldBlur('shippingState')}
            >
              <option value=''>-- Choose State --</option>
              {Object.entries(states[state.shippingCountry]).map(([code, name], index) => {
                return (
                  <option key={index} value={code}>
                    {name}
                  </option>
                );
              })}
            </select>
          </div>
          {errors.shippingState && <div className={styles.errorMessage}>{errors.shippingState}</div>}
        </div>
      );
    }

    return (
      <div className={styles.inputGroup}>
        <input
          placeholder='County/District/Region'
          type='text'
          name='shippingState'
          value={state.shippingState}
          className={errors.shippingState ? styles.errorInput : ''}
          onChange={(e) => {
            setState({ ...state, shippingState: e.target.value });
            setErrors((prevErrors) => ({ ...prevErrors, shippingState: null }));
          }}
          onBlur={() => handleFieldBlur('shippingState')}
        />

        {errors.shippingState && <div className={styles.errorMessage}>{errors.shippingState}</div>}
      </div>
    );
  }

  function renderBillingStateSelect() {
    if (state.billingCountry === 'US' || state.billingCountry === 'CA') {
      return (
        <div className={styles.inputGroup}>
          <div className={styles.formSelect}>
            <select
              name='billingState'
              value={state.billingState}
              onChange={(e) => {
                setState({ ...state, billingState: e.target.value });
                setErrors((prevErrors) => ({ ...prevErrors, billingState: null }));
              }}
              onBlur={() => handleFieldBlur('billingState')}
            >
              <option value=''>-- Choose State --</option>
              {Object.entries(states[state.billingCountry]).map(([code, name], index) => {
                return (
                  <option key={index} value={code}>
                    {name}
                  </option>
                );
              })}
            </select>
          </div>
          {errors.billingState && <div className={styles.errorMessage}>{errors.billingState}</div>}
        </div>
      );
    }

    return (
      <div className={styles.inputGroup}>
        <input
          placeholder='County/District/Region'
          type='text'
          name='billingState'
          value={state.billingState}
          className={errors.billingState ? styles.errorInput : ''}
          onChange={(e) => {
            setState({ ...state, billingState: e.target.value });
            setErrors((prevErrors) => ({ ...prevErrors, billingState: null }));
          }}
          onBlur={() => handleFieldBlur('billingState')}
        />

        {errors.billingState && <div className={styles.errorMessage}>{errors.billingState}</div>}
      </div>
    );
  }

  const maxDiscount = getMaxDiscount(offer);
  const displayDiscount = maxDiscount || 64;
  // const estimatedDeliveryStart = moment().add(3, 'days').format('ddd, MMM DD');
  // const estimatedDeliveryEnd = moment().add(10, 'days').format('ddd, MMM DD');
  // const shippingFrom =
  //   offer?.company?.city && offer?.company?.state
  //     ? `${offer.company.city}, ${offer.company.state}`
  //     : 'Los Angeles, CA';

  const arrowSvg = (
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 800' href='' srcset='' id='ib31p'>
      <path
        d='M524.4 0c5.8 1 9.9 5.4 14 9.8l62.2 65.3 55.9 58.4 51.3 53.7 44.3 46.4c13.6 14.2 27 28.6 40.8 42.5 9.7 9.8 9.4 31.2-.8 41.2-17.5 17.3-34.3 35.6-51.4 53.5l-57.3 59.9-49.8 52.2-47 49.1-45.2 47.5c-1.7 1.8-3.5 3.4-5 5.3-11.9 15-28.3 3.9-31.7-10.3-.7-3-.9-6.2-.9-9.3V439.9v-5.2c-8.4 0-16.5-.3-24.5.1-13.7.5-27.5 1.1-41.2 2.3a627.4 627.4 0 0 0-38.7 4.4c-13.9 2-27.8 4.3-41.6 6.9-24.1 4.5-47.8 11-71.3 19a465 465 0 0 0-95.1 44.7A411 411 0 0 0 22.6 717 372 372 0 0 0 .5 799.2c0 .3-.3.7-.4 1V782c.2-1.2.5-2.4.6-3.7 1.6-20.2 2.7-40.6 4.7-60.7 3.2-31.4 8.2-62.5 14.5-93.2A825.4 825.4 0 0 1 84.3 428c29.8-61.4 67.3-115 114.7-158.5a407 407 0 0 1 115.5-74.8 377.4 377.4 0 0 1 83.2-25.7 856 856 0 0 1 61.7-8.8c14-1.5 28.1-1.3 42.1-1.8.7 0 1.4-.2 2.3-.4v-5.1V26.8c0-8.5 2.3-15.6 8-20.5 3.3-2.8 7.6-4.1 11.5-6 .3-.3.7-.3 1.1-.3z'
        href=''
        srcset=''
      ></path>
    </svg>
  );
  return (
    <div className='bug-template' style={{ backgroundColor: 'whitesmoke' }}>
      <Banner banners={banners} discount={maxDiscount} />

      {/* Top Header Section */}
      <div className={styles.topHeader}>
        <div className={styles.topHeaderInner}>
          <div className={styles.topHeaderLogo}>
            <img src={offer.logoSrc || 'https://placehold.co/200x50?text=bugmd'} alt={offer.brand} />
          </div>
          <div className={styles.topHeaderRight}>
            <img src={ssl} alt='ssl' />

            <div className={styles.contactInfo}>
              <div className={styles.usFlag}>
                <img src={flag} alt='US Flag' />
              </div>
              <div className={styles.contactText}>
                <div>
                  <b>Contact Us:</b>
                </div>
                <div>{offer.company.email}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Framed Content */}
      <div className={styles.framedContent}>
        <div className={styles.heroSection}>
          <div className={styles.heroLeft}>
            {/* Image Slider */}
            <div className={styles.sliderContainer}>
              <div className={styles.mainSlide}>
                <img src={sliderImages[currentSlide]} alt={`Slide ${currentSlide + 1}`} />
              </div>

              {/* Thumbnails */}
              <div className={styles.thumbnailsContainer}>
                {sliderImages.map((img, index) => (
                  <div
                    key={index}
                    className={`${styles.thumbnail} ${currentSlide === index ? styles.thumbnailActive : ''}`}
                    onClick={() => setCurrentSlide(index)}
                  >
                    <img src={img} alt={`Thumbnail ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.heroRight}>
            <h1 className={styles.heroHeadline}>
              How To Get Up To 100+ TV Channels - No Monthly Bills or Subscriptions Ever.
            </h1>

            {/* Benefits with Green Arrows */}
            <div className={styles.benefitsList}>
              <div className={styles.benefitItem}>
                {arrowSvg}
                <span>Up to 100+ Free HD Channels</span>
              </div>
              <div className={styles.benefitItem}>
                {arrowSvg}
                <span>No Monthly Fees of Subscriptions</span>
              </div>
              <div className={styles.benefitItem}>
                {arrowSvg}
                <span>Compatible With Any TV.</span>
              </div>
              <div className={styles.benefitItem}>
                {arrowSvg}
                <span>Easy To Use - Just Plug It In & Start Enjoying TV.</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Urgency Banner */}
      <div className={styles.urgencyBanner}>
        <i className='fas fa-fire'></i>
        <span>
          {offer.productName} has been in high demand and inventory is limited. We have reserved your order
          for the next <strong>{formatTime(timeRemaining)}</strong>
        </span>
      </div>

      {/* Checkout Section - Two Column Layout */}
      <div className={styles.checkoutWrapper}>
        <div className={styles.checkoutLeft}>
          <div className={styles.selectionContainer}>
            {/* Purchase Options */}
            {/* <div className={styles.purchaseOptions}>
              <div className={`${styles.purchaseOption} ${styles.purchaseOptionActive}`}>
                <div className={styles.optionRadio}>
                  <span className={`${styles.radioBullet} ${styles.radioBulletActive}`}>
                    <i className='fas fa-check'></i>
                  </span>
                </div>
                <div className={styles.optionContent}>
                  <div className={styles.optionHeader}>
                    <span className={styles.optionTitle}>One-time purchase:</span>
                    <div className={styles.optionLogo}>
                      <img
                        src={offer.logoSrc || 'https://placehold.co/100x30?text=bugmd'}
                        alt={offer.brand}
                      />
                    </div>
                  </div>
                  <div className={styles.optionMeta}>
                    Deliver on: {estimatedDeliveryStart} - {estimatedDeliveryEnd}
                  </div>
                  <div className={styles.optionInStock}>In Stock</div>
                  <div className={styles.optionSecure}>
                    <i className='fas fa-lock'></i> Secure Transaction
                  </div>
                  <div className={styles.optionMeta}>Ships from: {shippingFrom}</div>
                </div>
              </div>

              <div className={styles.purchaseOption}>
                <div className={styles.optionRadio}>
                  <span className={styles.radioBullet}></span>
                </div>
                <div className={styles.optionContent}>
                  <div className={styles.optionHeader}>
                    <span className={styles.optionTitle}>Subscribe &amp; Save:</span>
                    <div className={styles.optionLogo}>
                      <img
                        src={offer.logoSrc || 'https://placehold.co/100x30?text=bugmd'}
                        alt={offer.brand}
                      />
                    </div>
                    <span className={styles.cancelBadge}>Cancel Anytime</span>
                  </div>
                  <div className={styles.optionSave}>SAVE AN EXTRA 15%</div>
                  <div className={styles.optionDescription}>
                    Get FREE US shipping &amp; savings on your first shipment then 16 pouches ship every 60
                    days at $6.60 each (FREE shipping, Cancel Anytime).
                  </div>
                  <div className={styles.optionMeta}>Deliver on: {estimatedDeliveryStart}</div>
                  <div className={styles.optionMeta}>Ships from: {shippingFrom}</div>
                </div>
              </div>
            </div> */}

            {/* Discount Notice Box */}
            <div className={styles.discountBox}>
              <div className={styles.discountBadge}>
                <div className={styles.discountBadgeRotating}></div>
                <div className={styles.discountBadgeInner}>
                  <span>Up to</span>
                  <span className={styles.discountPercent}>{displayDiscount} OFF</span>
                </div>
              </div>
              <div className={styles.discountTextWrapper}>
                <div className={styles.discountApplied}>
                  Your up to {displayDiscount}% OFF Discount Has Been Applied
                </div>
                <div className={styles.freeShippingLine}>
                  Your Order Qualifies For <b>FREE SHIPPING</b> When Ordered TODAY.
                </div>
              </div>
            </div>

            {/* Step 1 Products */}
            <div className={styles.stepProducts}>
              <h3 className={styles.stepTitle}>STEP 1: SELECT ORDER QUANTITY</h3>
              <div className={styles.productsLeft}>
                {mergedProductsData.map((product) => {
                  const isSelected = selectedProduct?.id === product.id;
                  const quantity = Number(product.quantity) || 1;
                  const productOldPrice = product.oldPrice ? Number(product.oldPrice) : null;
                  const productPrice = product.price ? Number(product.price) : 0;
                  const unitOldPrice = productOldPrice
                    ? getPriceWithCurrency(productOldPrice / quantity, offer?.locale, offer?.currency)
                    : null;
                  const unitPrice = getPriceWithCurrency(
                    productPrice / quantity,
                    offer?.locale,
                    offer?.currency
                  );
                  const shippingPrice = product.shippingPrice ? Number(product.shippingPrice) : 0;
                  const shippingText =
                    shippingPrice > 0
                      ? `${getPriceWithCurrency(shippingPrice, offer?.locale, offer?.currency)} Shipping`
                      : 'FREE SHIPPING';
                  const shippingClass = shippingPrice > 0 ? styles.rowShippingPaid : styles.rowShippingFree;
                  const discountPercent = getSavingsPercent(product.oldPrice, product.price);

                  return (
                    <div
                      key={product.id}
                      className={`${styles.product} ${isSelected ? styles.productActive : ''} ${
                        product.soldOut === '1' ? styles.productSoldOut : ''
                      } ${product.mostPopular ? styles.productBestSeller : ''}`}
                      onClick={() => product.soldOut !== '1' && setSelectedProduct(product)}
                    >
                      {product.mostPopular && <div className={styles.selectedArrow}></div>}
                      <div className={styles.productRadio}>
                        <span
                          className={`${styles.radioBullet} ${isSelected ? styles.radioBulletActive : ''}`}
                        >
                          <i className='fas fa-check'></i>
                        </span>
                      </div>
                      <div className={styles.productRowInner}>
                        <div className={styles.productRowLeft}>
                          <div className={styles.productDiscountLabel}>
                            {product.mostPopular && (
                              <b className={styles.productBestValueInline}>BEST SELLER</b>
                            )}
                            {product.soldOut === '1' && <span className={styles.soldOutBadge}>SOLD OUT</span>}
                            {discountPercent}% OFF: {product.quantity}{' '}
                            {product.quantity * 1 > 1 ? offer.productName : offer.productNameSingular}
                          </div>
                        </div>
                        <div className={styles.productRowRight}>
                          <div className={styles.priceLine}>
                            {unitOldPrice && <s className={styles.priceOld}>{unitOldPrice}</s>}
                            <span className={styles.priceValue}>{unitPrice}</span>
                            <span className={styles.priceEach}>Each</span>
                          </div>
                          <div className={`${styles.shippingLine} ${shippingClass}`}>{shippingText}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.checkoutRight}>
          <section className={styles.checkout}>
            {/* Step 2: Payment Method */}
            <div className={styles.step}>
              <h2 className={styles.stepTitle}>STEP 2: PAYMENT METHOD</h2>

              <div className={styles.paymentBox}>
                {/* Express Checkout - Apple Pay & Google Pay */}
                {clientToken && clientToken.startsWith('eyJ') && (showApplePay || showGooglePay) && (
                  <>
                    <div className={styles.expressCheckout}>
                      <span className={styles.expressCheckoutTitle}>Express Checkout</span>
                      <div className={styles.expressCheckoutButtons}>
                        {showApplePay &&
                          (offer.paymentGateway === 'nmi' ? (
                            <NmiApplePayButton
                              checkoutTotal={state.orderTotal}
                              orderData={state}
                              offer={offer}
                              onApprove={handleExpressApprove}
                            />
                          ) : (
                            <ApplePayButton
                              clientToken={clientToken}
                              checkoutTotal={state.orderTotal}
                              orderData={state}
                              offer={offer}
                              onApprove={handleExpressApprove}
                            />
                          ))}
                        {showGooglePay &&
                          (offer.paymentGateway === 'nmi' ? (
                            <NmiGooglePayButton
                              checkoutTotal={state.orderTotal}
                              orderData={state}
                              offer={offer}
                              onApprove={handleExpressApprove}
                            />
                          ) : (
                            <GooglePayButton
                              clientToken={clientToken}
                              checkoutTotal={state.orderTotal}
                              orderData={state}
                              offer={offer}
                              onApprove={handleExpressApprove}
                            />
                          ))}
                      </div>
                    </div>
                    <div className={styles.expressCheckoutDivider}>
                      <span>Or choose payment method</span>
                    </div>
                  </>
                )}

                {hasPayPal && (
                  <>
                    <button
                      type='button'
                      className={`${styles.paymentRow} ${
                        selectedPaymentMethod === 'paypal' ? styles.paymentRowActive : ''
                      }`}
                      onClick={() => setSelectedPaymentMethod('paypal')}
                    >
                      <div className={styles.paymentRowLeft}>
                        <span
                          className={`${styles.paymentRadio} ${
                            selectedPaymentMethod === 'paypal' ? styles.paymentRadioActive : ''
                          }`}
                        >
                          <i className='fas fa-check'></i>
                        </span>
                        <span className={styles.paymentLabel}>
                          <img src={paypal} alt='PayPal' className={styles.paymentPayPalIcon} />
                        </span>
                      </div>
                    </button>

                    {selectedPaymentMethod === 'paypal' && hasPayPal && (
                      <div className={styles.paymentRowContent}>
                        <div className={styles.paypalButtons}>
                          <PayPalErrorBoundary>
                            <PayPalScriptProvider
                              options={{
                                vault: true,
                                clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID,
                                components: 'buttons',
                                dataClientToken: clientToken,
                              }}
                            >
                              <PayPalButtons
                                offer={offer}
                                orderData={state}
                                checkoutTotal={state.orderTotal}
                                onApprove={handleExpressApprove}
                                isSelectedProductSubscription={false}
                              />
                            </PayPalScriptProvider>
                          </PayPalErrorBoundary>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <button
                  type='button'
                  className={`${styles.paymentRow} ${
                    selectedPaymentMethod === 'card' ? styles.paymentRowActive : ''
                  }`}
                  onClick={() => setSelectedPaymentMethod('card')}
                >
                  <div className={styles.paymentRowLeft}>
                    <span
                      className={`${styles.paymentRadio} ${
                        selectedPaymentMethod === 'card' ? styles.paymentRadioActive : ''
                      }`}
                    >
                      <i className='fas fa-check'></i>
                    </span>
                    <span className={styles.paymentLabel}>Credit Card</span>
                  </div>
                  <div className={styles.paymentRowRight}>
                    <img src={cc} alt='Credit Cards' className={styles.paymentCardIcons} />
                  </div>
                </button>
              </div>
            </div>

            {/* Step 3: Contact Information */}
            <div className={styles.step}>
              <h2 className={styles.stepTitle}>STEP 3: CONTACT INFORMATION</h2>

              <div className={styles.formGrid}>
                <div className={styles.inputGroup}>
                  <input
                    placeholder='First Name'
                    type='text'
                    name='firstName'
                    value={state.firstName}
                    className={errors.firstName ? styles.errorInput : ''}
                    onChange={(e) => {
                      setState({ ...state, firstName: e.target.value });
                      setErrors((prevErrors) => ({ ...prevErrors, firstName: null }));
                    }}
                    onBlur={() => handleFieldBlur('firstName')}
                  />
                  {errors.firstName && <div className={styles.errorMessage}>{errors.firstName}</div>}
                </div>

                <div className={styles.inputGroup}>
                  <input
                    placeholder='Last Name'
                    type='text'
                    name='lastName'
                    value={state.lastName}
                    className={errors.lastName ? styles.errorInput : ''}
                    onChange={(e) => {
                      setState({ ...state, lastName: e.target.value });
                      setErrors((prevErrors) => ({ ...prevErrors, lastName: null }));
                    }}
                    onBlur={() => handleFieldBlur('lastName')}
                  />
                  {errors.lastName && <div className={styles.errorMessage}>{errors.lastName}</div>}
                </div>

                <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
                  <input
                    placeholder='E-mail'
                    type='email'
                    name='email'
                    value={state.email}
                    className={errors.email ? styles.errorInput : ''}
                    onChange={(e) => {
                      setState({ ...state, email: e.target.value });
                      setErrors((prevErrors) => ({ ...prevErrors, email: null }));
                    }}
                    onBlur={() => handleFieldBlur('email')}
                  />
                  {errors.email && <div className={styles.errorMessage}>{errors.email}</div>}
                </div>

                <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
                  <div className={styles.phoneInputWrapper}>
                    <div className={styles.phonePrefix}>
                      <img src={flag} alt='US Flag' />
                      <span>+1</span>
                      <i className='fas fa-chevron-down'></i>
                    </div>
                    <input
                      placeholder='Phone'
                      type='tel'
                      name='phone'
                      value={state.phone.replace(/^\+44|^\+1/, '')}
                      className={errors.phone ? styles.errorInput : ''}
                      onChange={(e) => {
                        const prefix = '+1';
                        const phoneNumber = e.target.value.replace(/\D/g, '');
                        setState({ ...state, phone: `${prefix}${phoneNumber}` });
                        setErrors((prevErrors) => ({ ...prevErrors, phone: null }));
                      }}
                      onBlur={() => handleFieldBlur('phone')}
                    />
                  </div>
                  {errors.phone && <div className={styles.errorMessage}>{errors.phone}</div>}
                </div>

                <div className={styles.termsCheckbox} style={{ gridColumn: '1 / -1' }}>
                  <input
                    type='checkbox'
                    id='agreeSms'
                    name='agreeSms'
                    checked={state.agreeSms}
                    onChange={(e) => setState({ ...state, agreeSms: e.target.checked })}
                  />
                  <label htmlFor='agreeSms'>
                    Get 10% off your next order + insider deals when you sign up for texts and emails
                  </label>
                </div>
                {state.agreeSms && (
                  <div className={`${styles.smsText} ${styles.formFullWidth}`}>
                    By checking this box, you agree to receive recurring automated promotional and
                    personalized marketing text messages.
                  </div>
                )}

                {inlineReviews.length > 0 && (
                  <div className={`${styles.inlineReviewsSection} ${styles.formFullWidth}`}>
                    <div className={styles.inlineReviewsHeader}>
                      What Customers say about Vamoose Rodent Repellent
                    </div>
                    <div className={styles.inlineReviewsList}>
                      {inlineReviews.map((review) => {
                        const reviewInitial = (review.name || 'U').charAt(0);
                        const reviewDateMoment = review.createdAt
                          ? moment(review.createdAt)
                          : review.date
                          ? moment(review.date)
                          : null;
                        const reviewDate =
                          reviewDateMoment && reviewDateMoment.isValid()
                            ? reviewDateMoment.format('MM/DD/YY')
                            : '';

                        return (
                          <div key={review.id || review.name} className={styles.inlineReviewCard}>
                            <div className={styles.inlineReviewHeader}>
                              <div className={styles.inlineReviewAvatar}>
                                {review.imageSrc ? (
                                  <img src={review.imageSrc} alt={review.name || 'Customer'} />
                                ) : (
                                  <div className={styles.inlineReviewInitial}>{reviewInitial}</div>
                                )}
                                <div className={styles.inlineReviewVerifiedBadge}>
                                  <i className='fas fa-check'></i>
                                </div>
                              </div>
                              <div className={styles.inlineReviewMeta}>
                                <div className={styles.inlineReviewNameRow}>
                                  <span className={styles.inlineReviewName}>
                                    {review.name || 'Anonymous'}
                                  </span>
                                  <span className={styles.inlineReviewVerified}>Verified Buyer</span>
                                </div>
                                <div className={styles.inlineReviewStars}>
                                  {Array.from({ length: 5 }).map((_, starIdx) => (
                                    <i key={starIdx} className='fas fa-star'></i>
                                  ))}
                                </div>
                              </div>
                            </div>
                            {review.title && <div className={styles.inlineReviewTitle}>{review.title}</div>}
                            <div className={styles.inlineReviewText}>{review.text || 'Great product!'}</div>
                            {reviewDate && <div className={styles.inlineReviewDate}>{reviewDate}</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Step 4: Shipping Address */}
            <div className={styles.step}>
              <h2 className={styles.stepTitle}>STEP 4: SHIPPING ADDRESS</h2>

              <div className={styles.formGrid}>
                <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
                  <input
                    placeholder='Street Address'
                    type='text'
                    name='shippingAddress1'
                    value={state.shippingAddress1}
                    className={errors.shippingAddress1 ? styles.errorInput : ''}
                    onChange={(e) => {
                      setState({ ...state, shippingAddress1: e.target.value });
                      setErrors((prevErrors) => ({ ...prevErrors, shippingAddress1: null }));
                    }}
                    onBlur={() => handleFieldBlur('shippingAddress1')}
                  />
                  {errors.shippingAddress1 && (
                    <div className={styles.errorMessage}>{errors.shippingAddress1}</div>
                  )}
                </div>

                <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
                  <input placeholder='Apartment or Suite (Optional)' type='text' />
                </div>

                <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
                  <input
                    placeholder='City'
                    type='text'
                    name='shippingCity'
                    value={state.shippingCity}
                    className={errors.shippingCity ? styles.errorInput : ''}
                    onChange={(e) => {
                      setState({ ...state, shippingCity: e.target.value });
                      setErrors((prevErrors) => ({ ...prevErrors, shippingCity: null }));
                    }}
                    onBlur={() => handleFieldBlur('shippingCity')}
                  />
                  {errors.shippingCity && <div className={styles.errorMessage}>{errors.shippingCity}</div>}
                </div>

                <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
                  <select
                    name='shippingCountry'
                    value={state.shippingCountry}
                    onChange={(e) => setState({ ...state, shippingCountry: e.target.value })}
                  >
                    <option value='US'>United States</option>
                  </select>
                </div>

                {renderStateSelect()}

                <div className={styles.inputGroup}>
                  <input
                    placeholder='Zip Code'
                    type='text'
                    name='shippingZip'
                    value={state.shippingZip}
                    className={errors.shippingZip ? styles.errorInput : ''}
                    onChange={(e) => {
                      setState({ ...state, shippingZip: e.target.value });
                      setErrors((prevErrors) => ({ ...prevErrors, shippingZip: null }));
                    }}
                    onBlur={() => handleFieldBlur('shippingZip')}
                  />
                  {errors.shippingZip && <div className={styles.errorMessage}>{errors.shippingZip}</div>}
                </div>

                <div
                  className={`${styles.inputGroup} ${styles.inputWithIcon}`}
                  style={{ gridColumn: '1 / -1' }}
                >
                  <NumberFormat
                    name='creditCardNumber'
                    format='#### #### #### ####'
                    placeholder='Credit Card Number'
                    value={state.creditCardNumber}
                    className={errors.creditCardNumber ? styles.errorInput : ''}
                    onValueChange={(values) => {
                      const { value } = values;
                      setState({ ...state, creditCardNumber: value });
                      setErrors((prevErrors) => ({ ...prevErrors, creditCardNumber: null }));
                    }}
                    onBlur={() => handleFieldBlur('creditCardNumber')}
                  />
                  <i className='fas fa-lock'></i>
                  {errors.creditCardNumber && (
                    <div className={styles.errorMessage}>{errors.creditCardNumber}</div>
                  )}
                </div>

                <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
                  <NumberFormat
                    name='CVV'
                    format='####'
                    placeholder='Security Code (3-4 Digits)'
                    value={state.CVV}
                    className={errors.CVV ? styles.errorInput : ''}
                    onValueChange={(values) => {
                      const { value } = values;
                      setState({ ...state, CVV: value });
                      setErrors((prevErrors) => ({ ...prevErrors, CVV: null }));
                    }}
                    onBlur={() => handleFieldBlur('CVV')}
                  />
                  {errors.CVV && <div className={styles.errorMessage}>{errors.CVV}</div>}
                </div>

                <div className={styles.inputGroup}>
                  <select
                    name='expirationMonth'
                    value={state.expirationMonth}
                    onChange={(e) => {
                      setState({ ...state, expirationMonth: e.target.value });
                      setErrors((prevErrors) => ({ ...prevErrors, expirationMonth: null }));
                    }}
                    onBlur={() => handleFieldBlur('expirationMonth')}
                    className={errors.expirationMonth ? styles.errorInput : ''}
                  >
                    <option value=''>Card Month</option>
                    {Array.from({ length: 12 }, (_, i) => {
                      const month = String(i + 1).padStart(2, '0');
                      return (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      );
                    })}
                  </select>
                  {errors.expirationMonth && (
                    <div className={styles.errorMessage}>{errors.expirationMonth}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <select
                    name='expirationYear'
                    value={state.expirationYear}
                    onChange={(e) => {
                      setState({ ...state, expirationYear: e.target.value });
                      setErrors((prevErrors) => ({ ...prevErrors, expirationYear: null }));
                    }}
                    onBlur={() => handleFieldBlur('expirationYear')}
                    className={errors.expirationYear ? styles.errorInput : ''}
                  >
                    <option value=''>Exp Year</option>
                    {Array.from({ length: 20 }, (_, i) => {
                      const year = new Date().getFullYear() + i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                  {errors.expirationYear && (
                    <div className={styles.errorMessage}>{errors.expirationYear}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Step 5: Billing Address */}
            <div className={styles.step}>
              <h2 className={styles.stepTitle}>STEP 5: BILLING ADDRESS</h2>

              <div className={styles.billingOptions}>
                <label className={styles.radioOption}>
                  <input
                    type='radio'
                    checked={state.billingSameAsShipping}
                    onChange={() => setState({ ...state, billingSameAsShipping: true })}
                  />
                  <span
                    className={`${styles.radioBullet} ${
                      state.billingSameAsShipping ? styles.radioBulletActive : ''
                    }`}
                  >
                    <i className='fas fa-check'></i>
                  </span>
                  <span>Same as shipping address.</span>
                </label>

                <label className={styles.radioOption}>
                  <input
                    type='radio'
                    checked={!state.billingSameAsShipping}
                    onChange={() => setState({ ...state, billingSameAsShipping: false })}
                  />
                  <span
                    className={`${styles.radioBullet} ${
                      !state.billingSameAsShipping ? styles.radioBulletActive : ''
                    }`}
                  >
                    <i className='fas fa-check'></i>
                  </span>
                  <span>Use a different billing address.</span>
                </label>
              </div>

              {!state.billingSameAsShipping && (
                <div className={styles.formGrid}>
                  <div className={styles.inputGroup}>
                    <input
                      placeholder='First Name'
                      type='text'
                      name='billingFirstName'
                      value={state.billingFirstName}
                      className={errors.billingFirstName ? styles.errorInput : ''}
                      onChange={(e) => {
                        setState({ ...state, billingFirstName: e.target.value });
                        setErrors((prevErrors) => ({ ...prevErrors, billingFirstName: null }));
                      }}
                      onBlur={() => handleFieldBlur('billingFirstName')}
                    />
                    {errors.billingFirstName && (
                      <div className={styles.errorMessage}>{errors.billingFirstName}</div>
                    )}
                  </div>
                  <div className={styles.inputGroup}>
                    <input
                      placeholder='Last Name'
                      type='text'
                      name='billingLastName'
                      value={state.billingLastName}
                      className={errors.billingLastName ? styles.errorInput : ''}
                      onChange={(e) => {
                        setState({ ...state, billingLastName: e.target.value });
                        setErrors((prevErrors) => ({ ...prevErrors, billingLastName: null }));
                      }}
                      onBlur={() => handleFieldBlur('billingLastName')}
                    />
                    {errors.billingLastName && (
                      <div className={styles.errorMessage}>{errors.billingLastName}</div>
                    )}
                  </div>

                  <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
                    <input
                      placeholder='Street Address'
                      type='text'
                      name='billingAddress1'
                      value={state.billingAddress1}
                      className={errors.billingAddress1 ? styles.errorInput : ''}
                      onChange={(e) => {
                        setState({ ...state, billingAddress1: e.target.value });
                        setErrors((prevErrors) => ({ ...prevErrors, billingAddress1: null }));
                      }}
                      onBlur={() => handleFieldBlur('billingAddress1')}
                    />
                    {errors.billingAddress1 && (
                      <div className={styles.errorMessage}>{errors.billingAddress1}</div>
                    )}
                  </div>

                  <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
                    <input
                      placeholder='City'
                      type='text'
                      name='billingCity'
                      value={state.billingCity}
                      className={errors.billingCity ? styles.errorInput : ''}
                      onChange={(e) => {
                        setState({ ...state, billingCity: e.target.value });
                        setErrors((prevErrors) => ({ ...prevErrors, billingCity: null }));
                      }}
                      onBlur={() => handleFieldBlur('billingCity')}
                    />
                    {errors.billingCity && <div className={styles.errorMessage}>{errors.billingCity}</div>}
                  </div>

                  <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
                    <div className={styles.formSelect}>
                      <select
                        name='billingCountry'
                        value={state.billingCountry}
                        onChange={(e) => {
                          setState({ ...state, billingCountry: e.target.value });
                          setErrors((prevErrors) => ({ ...prevErrors, billingCountry: null }));
                        }}
                        onBlur={() => handleFieldBlur('billingCountry')}
                      >
                        {offer.shippingCountries.map((country, index) => (
                          <option key={index} value={country.id}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {renderBillingStateSelect()}
                  <div className={styles.inputGroup}>
                    <input
                      placeholder='Zip Code'
                      type='text'
                      name='billingZip'
                      value={state.billingZip}
                      className={errors.billingZip ? styles.errorInput : ''}
                      onChange={(e) => {
                        setState({ ...state, billingZip: e.target.value });
                        setErrors((prevErrors) => ({ ...prevErrors, billingZip: null }));
                      }}
                      onBlur={() => handleFieldBlur('billingZip')}
                    />
                    {errors.billingZip && <div className={styles.errorMessage}>{errors.billingZip}</div>}
                  </div>
                </div>
              )}
            </div>

            {/* Step 6: Order Summary */}
            <div className={styles.step}>
              <h2 className={styles.stepTitle}>STEP 6: ORDER SUMMARY</h2>

              <div className={styles.orderSummary}>
                <button
                  onClick={() => handleFormSubmit()}
                  className={styles.submitButton}
                  type='button'
                  disabled={isLoading || isCooldownActive}
                >
                  YES! Send me my {offer.productNameSingular}!
                  {isLoading && <i className='fas fa-spinner fa-spin'></i>}
                </button>

                {isCooldownActive && (
                  <div className={styles.declineError}>
                    <p>
                      You've had multiple declined payment attempts. Please wait 15 minutes before trying
                      again.
                    </p>
                  </div>
                )}

                {declineError && !isCooldownActive && (
                  <div className={styles.declineError}>
                    <p>{declineError}</p>
                  </div>
                )}

                <div className={styles.trustBadges}>
                  <div className={styles.securityBadges}>
                    <img src={norton} alt='Norton Secure' />
                  </div>
                  <div className={styles.trustBadge}>
                    <i className='fas fa-lock'></i>
                    <span>Secure 256-bit SSL encryption</span>
                  </div>
                  <div className={styles.guarantee}>
                    <img src={sat} alt='30 Day Satisfaction Guarantee' />
                    <p>
                      We have a 30 day satisfaction guarantee, we want our customers happy! Please refer to
                      our return policy at the bottom of the page for more details. Happy Shopping!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* FAQ Section */}
      <div className={styles.container}>
        <section className={styles.faqSection}>
          <h2 className={styles.faqTitle}>Frequently Asked Questions</h2>

          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>How many channels will I get?</div>
            <div className={styles.faqAnswer}>
              You'll receive all available over-the-air broadcasts in your area, typically 100+ channels
              including major networks like ABC, CBS, NBC, FOX, PBS, and many local channels. The exact number
              depends on your location and proximity to broadcast towers.
            </div>
          </div>

          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>Is installation really that easy?</div>
            <div className={styles.faqAnswer}>
              Yes! Simply connect the antenna to your TV's coaxial input, run a channel scan, and you're done.
              The entire process takes under a minute. No tools required, no professional installation needed.
            </div>
          </div>

          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>Will this work in my area?</div>
            <div className={styles.faqAnswer}>
              UltraLink works anywhere there are over-the-air broadcasts, which covers 99% of the US
              population. Our 120+ mile range ensures strong signal reception even in rural areas. If you're
              unsure, remember our 60-day money-back guarantee!
            </div>
          </div>

          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>What if I'm not satisfied?</div>
            <div className={styles.faqAnswer}>
              We offer a full 60-day money-back guarantee. If you're not completely satisfied with UltraLink
              for any reason, simply return it for a full refund. No questions asked, no restocking fees.
            </div>
          </div>

          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>How is the picture quality?</div>
            <div className={styles.faqAnswer}>
              Over-the-air signals are actually uncompressed, so you'll get better HD picture quality than
              cable or satellite! Many customers report that local channels look sharper and clearer than they
              ever did with their cable provider.
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
